import { call, put, takeLatest } from 'redux-saga/effects';
import { HealthClaim } from '@models/HealthClaim';
import { HealthClaimApi } from '@apis/HealthClaimApi';
import { BenefitLimit } from '@models/BenefitLimit';
import { BenefitLimitApi } from '@apis/BenefitLimitApi';
import { PolicyBenefitLimit } from '@models/PolicyBenefitLimit';
import { PolicyBenefitLimitApi } from '@apis/PolicyBenefitLimitApi';
import { SagaPayload } from '@utils/typescript';
import {
  benefitLimitsFetch,
  BenefitLimitsFetchPayload,
  policyBenefitLimitsFetch,
  PolicyBenefitLimitsFetchPayload,
  healthClaimCostItemsFetch,
  HealthClaimCostItemsFetchPayload,
  healthClaimUpdate,
  HealthClaimUpdatePayload,
  healthClaimSettlementLetterNotesUpdate,
  HealthClaimSettlementLetterNotesUpdatePayload,
  healthClaimCostItemCreate,
  HealthClaimCostItemCreatePayload,
  healthClaimCostItemCreateSuccess,
  healthClaimCostItemUpdate,
  HealthClaimCostItemUpdatePayload,
  healthClaimCostItemUpdateSuccess,
  healthClaimCostItemDelete,
  HealthClaimCostItemDeletePayload,
  healthClaimCostItemDeleteSuccess,
  healthClaimCostItemsApprove,
  HealthClaimCostItemsApprovePayload,
  healthClaimFetch,
  HealthClaimFetchPayload,
  healthClaimUploadDocuments,
  HealthClaimUploadDocumentsPayload,
  healthClaimApprove,
  HealthClaimApprovePayload,
  diagnosisCodeSearch,
  DiagnosisCodeSearchPayload,
  healthClaimBillingItemsFetch,
  HealthClaimBillingItemsFetchPayload,
  healthClaimBillingItemsBulkCreate,
  HealthClaimBillingItemsBulkCreatePayload,
  healthClaimBillingItemUpdate,
  HealthClaimBillingItemUpdatePayload,
  healthClaimBillingItemDelete,
  HealthClaimBillingItemDeletePayload,
  billingItemsFetch,
  BillingItemsFetchPayload,
  stateUpdate,
  StateUpdatePayload,
} from './action';
import { HealthClaimCostItemApi } from '@apis/HealthClaimCostItemApi';
import { HealthClaimCostItem } from '@models/HealthClaimCostItem';
import { HealthClaimBillingItemApi } from '@apis/HealthClaimBillingItemApi';
import { HealthClaimBillingItem } from '@models/HealthClaimBillingItem';
import { BillingItemApi } from '@apis/BillingItemApi';
import { BillingItemWithPrice } from '@models/BillingItemWithPrice';
import { DiagnosisCodeApi } from '@apis/DiagnosisCodeApi';
import { DiagnosisCode } from '@models/DiagnosisCode';
import { getResponseError } from '@utils/error';
import { AxiosError } from 'axios';

function* handleHealthClaimFetch({ payload }: SagaPayload<HealthClaimFetchPayload>) {
  const state: StateUpdatePayload = { healthClaim: null };
  try {
    state.healthClaim = (yield call(HealthClaimApi.get, payload)) as HealthClaim;
    yield put(stateUpdate(state));
  } catch {
    yield put(stateUpdate(state));
  }
}

function* handleHealthClaimUpdate({ payload }: SagaPayload<HealthClaimUpdatePayload>) {
  yield put(stateUpdate({ isUpdatingHealthClaim: true }));

  try {
    const state: StateUpdatePayload = { healthClaim: null };
    state.healthClaim = (yield call(HealthClaimApi.update, payload)) as HealthClaim;
    yield put(stateUpdate({ ...state, isUpdatingHealthClaim: false }));
  } catch (e) {
    const serverErrors = getResponseError(e as AxiosError).errors;
    const state: StateUpdatePayload = { serverErrors };
    yield put(stateUpdate({ ...state, isUpdatingHealthClaim: false }));
  }
}

function* handleHealthClaimSettlementLetterNotesUpdate({ payload }: SagaPayload<HealthClaimSettlementLetterNotesUpdatePayload>) {
  try {
    const state: StateUpdatePayload = { healthClaim: null };
    state.healthClaim = (yield call(HealthClaimApi.updateSettlementLetterNotes, payload)) as HealthClaim;
    yield put(stateUpdate(state));
  } catch { }
}

function* handleHealthClaimUploadDocuments({ payload }: SagaPayload<HealthClaimUploadDocumentsPayload>) {
  try {
    const state: StateUpdatePayload = { healthClaim: null };
    state.healthClaim = (yield call(HealthClaimApi.uploadDocuments, payload)) as HealthClaim;
    yield put(stateUpdate(state));
  } catch { }
}

function* handleHealthClaimApprove({ payload }: SagaPayload<HealthClaimApprovePayload>) {
  try {
    const state: StateUpdatePayload = { healthClaim: null };
    state.healthClaim = (yield call(HealthClaimApi.approve, payload)) as HealthClaim;
    yield put(stateUpdate(state));
  } catch { }
}

function* handleBenefitLimitsFetch({ payload }: SagaPayload<BenefitLimitsFetchPayload>) {
  const state: StateUpdatePayload = { benefitLimits: null };
  try {
    state.benefitLimits = (yield call(BenefitLimitApi.getAll, payload)) as BenefitLimit[];
    yield put(stateUpdate(state));
  } catch {
    yield put(stateUpdate(state));
  }
}

function* handlePolicyBenefitLimitsFetch({ payload }: SagaPayload<PolicyBenefitLimitsFetchPayload>) {
  const state: StateUpdatePayload = { policyBenefitLimits: null };
  try {
    state.policyBenefitLimits = (yield call(PolicyBenefitLimitApi.getAll, payload)) as PolicyBenefitLimit[];
    yield put(stateUpdate(state));
  } catch {
    yield put(stateUpdate(state));
  }
}

function* handleHealthClaimCostItemsFetch({ payload }: SagaPayload<HealthClaimCostItemsFetchPayload>) {
  const state: StateUpdatePayload = { healthClaimCostItems: null };
  try {
    state.healthClaimCostItems = (yield call(HealthClaimCostItemApi.getAll, payload)) as HealthClaimCostItem[];
    yield put(stateUpdate(state));
  } catch {
    yield put(stateUpdate(state));
  }
}

function* handleHealthClaimCostItemCreate({ payload }: SagaPayload<HealthClaimCostItemCreatePayload>) {
  try {
    const healthClaimCostItem = (yield call(HealthClaimCostItemApi.create, payload)) as HealthClaimCostItem;
    yield put(healthClaimCostItemCreateSuccess({ healthClaimCostItem }))
  } catch { }
}

function* handleHealthClaimCostItemUpdate({ payload }: SagaPayload<HealthClaimCostItemUpdatePayload>) {
  try {
    const healthClaimCostItem = (yield call(HealthClaimCostItemApi.update, payload)) as HealthClaimCostItem;
    yield put(healthClaimCostItemUpdateSuccess({ healthClaimCostItem }));
  } catch { }
}

function* handleHealthClaimCostItemDelete({ payload }: SagaPayload<HealthClaimCostItemDeletePayload>) {
  try {
    const healthClaimCostItem = (yield call(HealthClaimCostItemApi.delete, payload)) as HealthClaimCostItem;
    yield put(healthClaimCostItemDeleteSuccess({ healthClaimCostItem }));
  } catch { }
}

function* handleHealthClaimCostItemsApprove({ payload }: SagaPayload<HealthClaimCostItemsApprovePayload>) {
  try {
    const state: StateUpdatePayload = { healthClaimCostItems: null };
    state.healthClaimCostItems = (yield call(HealthClaimCostItemApi.approveAll, payload)) as HealthClaimCostItem[];
    yield put(stateUpdate(state));
  } catch { }
}

function* handleDiagnosisCodeSearch({ payload }: SagaPayload<DiagnosisCodeSearchPayload>) {
  yield put(stateUpdate({ isDiagnosisCodeSearching: true }));
  try {
    const diagnosisCodeOptions = (yield call(DiagnosisCodeApi.search, payload.search)) as DiagnosisCode[];
    yield put(stateUpdate({ diagnosisCodeOptions, isDiagnosisCodeSearching: false }));
  } catch {
    yield put(stateUpdate({ diagnosisCodeOptions: [], isDiagnosisCodeSearching: false }));
  }
}

function* handleHealthClaimBillingItemsFetch({ payload }: SagaPayload<HealthClaimBillingItemsFetchPayload>) {
  const state: StateUpdatePayload = { healthClaimBillingItems: null };
  try {
    state.healthClaimBillingItems = (yield call(HealthClaimBillingItemApi.getAll, payload)) as HealthClaimBillingItem[];
    yield put(stateUpdate(state));
  } catch {
    yield put(stateUpdate(state));
  }
}

function* handleHealthClaimBillingItemsBulkCreate({ payload }: SagaPayload<HealthClaimBillingItemsBulkCreatePayload>) {
  try {
    yield call(HealthClaimBillingItemApi.bulkCreate, payload);
    yield put(healthClaimBillingItemsFetch({ healthClaimId: payload.healthClaimId }));
  } catch { }
}

function* handleHealthClaimBillingItemUpdate({ payload }: SagaPayload<HealthClaimBillingItemUpdatePayload>) {
  try {
    const updated = (yield call(HealthClaimBillingItemApi.update, payload)) as HealthClaimBillingItem;
    if (updated.healthClaimId) {
      yield put(healthClaimBillingItemsFetch({ healthClaimId: updated.healthClaimId }));
    }
  } catch { }
}

function* handleHealthClaimBillingItemDelete({ payload }: SagaPayload<HealthClaimBillingItemDeletePayload>) {
  try {
    const deleted = (yield call(HealthClaimBillingItemApi.delete, payload)) as HealthClaimBillingItem;
    if (deleted.healthClaimId) {
      yield put(healthClaimBillingItemsFetch({ healthClaimId: deleted.healthClaimId }));
    }
  } catch { }
}

function* handleBillingItemsFetch({ payload }: SagaPayload<BillingItemsFetchPayload>) {
  const state: StateUpdatePayload = { billingItems: null };
  try {
    state.billingItems = (yield call(BillingItemApi.getAll, payload)) as BillingItemWithPrice[];
    yield put(stateUpdate(state));
  } catch {
    yield put(stateUpdate(state));
  }
}

export default function* () {
  yield takeLatest(healthClaimFetch, handleHealthClaimFetch);
  yield takeLatest(healthClaimUpdate, handleHealthClaimUpdate);
  yield takeLatest(healthClaimSettlementLetterNotesUpdate, handleHealthClaimSettlementLetterNotesUpdate);
  yield takeLatest(healthClaimUploadDocuments, handleHealthClaimUploadDocuments);
  yield takeLatest(healthClaimApprove, handleHealthClaimApprove);
  yield takeLatest(benefitLimitsFetch, handleBenefitLimitsFetch);
  yield takeLatest(policyBenefitLimitsFetch, handlePolicyBenefitLimitsFetch);
  yield takeLatest(healthClaimCostItemsFetch, handleHealthClaimCostItemsFetch);
  yield takeLatest(healthClaimCostItemCreate, handleHealthClaimCostItemCreate);
  yield takeLatest(healthClaimCostItemUpdate, handleHealthClaimCostItemUpdate);
  yield takeLatest(healthClaimCostItemDelete, handleHealthClaimCostItemDelete);
  yield takeLatest(healthClaimCostItemsApprove, handleHealthClaimCostItemsApprove);
  yield takeLatest(diagnosisCodeSearch, handleDiagnosisCodeSearch);
  yield takeLatest(healthClaimBillingItemsFetch, handleHealthClaimBillingItemsFetch);
  yield takeLatest(healthClaimBillingItemsBulkCreate, handleHealthClaimBillingItemsBulkCreate);
  yield takeLatest(healthClaimBillingItemUpdate, handleHealthClaimBillingItemUpdate);
  yield takeLatest(healthClaimBillingItemDelete, handleHealthClaimBillingItemDelete);
  yield takeLatest(billingItemsFetch, handleBillingItemsFetch);
}
